/* eslint-disable max-lines -- large form: destination/collection address lookups and actions */
import { useCallback, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import { Field, Form } from 'react-final-form';
import Button from '@mui/material/Button';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { cloneDeep, get, set } from 'lodash';
import PropTypes from 'prop-types';
import { required } from '../../helpers/validators';
import AddressLookup from '../AddressLookup/AddressLookup';
import CountriesSelect from '../CountriesSelect';
import { FORM, ROUTE_PATH, STRINGS } from '../../constants';
import { SEND_A_CITYSPRINT_PARCEL } from '../../constants/routes';
import { FormInput } from '../FormInput';
import {
  AddressHelpers,
  NetworkHelpers,
  NormalizerHelpers,
  Validators,
} from '../../helpers';
import { enterCitysprintWizardFromQuickQuote } from '../../pages/SendCitysprintParcel/redux';
import { StringUtil } from '../../utils';
import ServicePricesModal from '../ServicePricesModal';
import { networksActions } from '../../redux/networksSlice';
import { countriesSelectors } from '../../redux/countriesSlice';
import configSlice from '../../redux/configSlice';

const validateCollectionAddressWhenSameDay = (value, allValues) =>
  allValues?.sameDayDelivery ? required(value) : undefined;

const QuoteForm = ({ priceWithVatRateText, priceWithoutVatRateText }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const countries = useSelector(countriesSelectors.getCountries);
  const { sameday } = useSelector(configSlice.selectors.getFeatureConfig);
  const [countryISO, setCountryISO] = useState(STRINGS.GB);
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const [quoteValues, setQuoteValues] = useState({});
  const [isInternational, setIsInternational] = useState(false);

  const initialValues = useMemo(
    () => ({
      sameDayDelivery: false,
      outboundConsignment: {
        deliveryDetails: {
          address: {
            countryCode: STRINGS.GB,
          },
        },
        collectionDetails: {
          address: {
            countryCode: STRINGS.GB,
            town: 'Oldbury',
            county: 'West Midlands',
            postcode: 'B69 4DA',
          },
        },
      },
    }),
    []
  );

  const handleQuoteFormSubmit = useCallback(
    values => {
      if (values.sameDayDelivery) {
        dispatch(enterCitysprintWizardFromQuickQuote(values));
        navigate(SEND_A_CITYSPRINT_PARCEL);
        return;
      }

      let query = `/quotes?${FORM.FIELDS.DESTINATION_COUNTRY.KEY}=${get(
        values,
        FORM.SHIPMENT_FIELDS.DESTINATION_COUNTRY.KEY
      )}`;
      const destinationAddressKey = values[FORM.FIELDS.ADDRESS_KEY.KEY];
      const weight = values[FORM.FIELDS.WEIGHT.KEY];
      const networkCode = values[FORM.FIELDS.NETWORK_CODE.KEY];

      if (destinationAddressKey) {
        query += `&${FORM.FIELDS.ADDRESS_KEY.KEY}=${destinationAddressKey}`;
      }
      if (weight) {
        query += `&${FORM.FIELDS.WEIGHT.KEY}=${weight}`;
      }
      if (networkCode) {
        query += `&${FORM.FIELDS.NETWORK_CODE.KEY}=${networkCode}`;
      }
      navigate(query);
    },
    [dispatch, navigate]
  );

  const handleQuickQuoteClick = useCallback(
    async values => {
      const fullData = cloneDeep(values);
      const countryCode = get(
        values,
        FORM.SHIPMENT_FIELDS.DESTINATION_COUNTRY.KEY
      );
      const town = get(values, FORM.SHIPMENT_FIELDS.DESTINATION_CITY.KEY);
      const county = get(values, FORM.SHIPMENT_FIELDS.DESTINATION_COUNTY.KEY);
      const postcode = get(
        values,
        FORM.SHIPMENT_FIELDS.DESTINATION_POSTCODE.KEY
      );

      if (countryCode === STRINGS.GB) {
        if (!town) {
          set(fullData, FORM.SHIPMENT_FIELDS.DESTINATION_CITY.KEY, 'Oldbury');
        }
        if (!county) {
          set(
            fullData,
            FORM.SHIPMENT_FIELDS.DESTINATION_COUNTY.KEY,
            'West Midlands'
          );
        }
        if (!postcode) {
          set(
            fullData,
            FORM.SHIPMENT_FIELDS.DESTINATION_POSTCODE.KEY,
            'B69 4DA'
          );
        }
      }

      const networksQuery = NetworkHelpers.getNetworkQuery(fullData);
      const networksError = Validators.validateNetworkQuery({
        networksQuery,
        countries,
        deliveryCountryCode: countryCode,
        collectionCountryCode: STRINGS.GB,
      });

      if (!networksError && !values.sameDayDelivery) {
        await dispatch(networksActions.fetchNetworks(networksQuery));
      }

      setQuoteValues(values);
      setServiceModalOpen(true);
    },
    [countries, dispatch]
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Form
        onSubmit={handleQuoteFormSubmit}
        initialValues={initialValues}
        // eslint-disable-next-line complexity -- quick quote + same-day branch UI
        render={({
          handleSubmit,
          submitting,
          invalid,
          form,
          values,
          errors,
        }) => {
          const sameDayDelivery = !!values.sameDayDelivery;
          const sameDayAddressesIncomplete =
            sameDayDelivery &&
            (!get(values, FORM.FIELDS.ADDRESS_KEY.KEY) ||
              !get(values, FORM.SHIPMENT_FIELDS.COLLECTION_ADDRESS_KEY.KEY));
          const destinationCountries = sameDayDelivery
            ? countries.filter(country => country.countryKey === STRINGS.GB)
            : countries;

          return (
            <Paper
              elevation={1}
              sx={{
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow:
                  '0px 1px 3px 0px rgba(0,0,0,0.12), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 2px 1px -1px rgba(0,0,0,0.2)',
              }}
            >
              <Box
                component='form'
                noValidate
                autoComplete='off'
                onSubmit={handleSubmit}
                sx={{ display: 'flex', flexDirection: 'column' }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    flexWrap: 'wrap',
                    alignItems: { xs: 'flex-start', sm: 'flex-end' },
                    gap: { xs: 0.5, sm: 1 },
                    px: 2,
                    pt: 2,
                    pb: 1,
                  }}
                >
                  <Typography
                    component='h2'
                    sx={{
                      fontSize: { xs: 28, sm: 34 },
                      lineHeight: 1.167,
                      fontWeight: 400,
                      color: 'text.primary',
                    }}
                  >
                    Send a parcel now
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'baseline',
                      gap: 0.5,
                    }}
                  >
                    <Typography
                      component='span'
                      sx={{
                        fontSize: { xs: 24, sm: 28 },
                        lineHeight: 1.2,
                        fontWeight: 300,
                        color: 'text.primary',
                      }}
                    >
                      from{' '}
                    </Typography>
                    <Typography
                      component='span'
                      sx={{
                        fontSize: { xs: 24, sm: 28 },
                        lineHeight: 1.2,
                        fontWeight: 700,
                        color: 'text.primary',
                      }}
                    >
                      {priceWithVatRateText}{' '}
                    </Typography>
                    {priceWithoutVatRateText != null && (
                      <Typography
                        component='span'
                        sx={{
                          fontSize: { xs: 14, sm: 16 },
                          lineHeight: 1.2,
                          fontWeight: 300,
                          color: 'text.primary',
                        }}
                      >
                        ({priceWithoutVatRateText} plus VAT)
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: { xs: 1.5, md: 1.5 },
                    px: 2,
                    pt: 1.5,
                    pb: 0.5,
                  }}
                >
                  <Field
                    name={FORM.SHIPMENT_FIELDS.DESTINATION_COUNTRY.KEY}
                    validate={required}
                    aria-controls='form-destination-country'
                  >
                    {props => (
                      <FormControl
                        variant='outlined'
                        size='small'
                        sx={{
                          minWidth: { md: 195, xs: '100%' },
                          flexGrow: { xs: 1, md: 0 },
                        }}
                        id='form-control-destination-country'
                        aria-controls='form-control-destination-country'
                      >
                        <InputLabel
                          htmlFor={`${FORM.FIELDS.DESTINATION_COUNTRY.KEY}_select`}
                          id={`${FORM.FIELDS.DESTINATION_COUNTRY.KEY}_label`}
                        >
                          {FORM.FIELDS.DESTINATION_COUNTRY.LABEL}
                        </InputLabel>
                        <CountriesSelect
                          component={Select}
                          name={FORM.SHIPMENT_FIELDS.DESTINATION_COUNTRY.KEY}
                          countries={destinationCountries}
                          id={`${FORM.FIELDS.DESTINATION_COUNTRY.KEY}_select`}
                          labelId={`${FORM.FIELDS.DESTINATION_COUNTRY.KEY}_label`}
                          label={FORM.FIELDS.DESTINATION_COUNTRY.LABEL}
                          size='small'
                          blurOnSelect
                          select
                          autoComplete='off'
                          fullWidth
                          disabled={
                            !destinationCountries.length || sameDayDelivery
                          }
                          MenuProps={{
                            PaperProps: { sx: { maxHeight: 500 } },
                          }}
                          onClose={props.input.onBlur}
                          {...props.input}
                          onChange={event => {
                            setCountryISO(event.target.value);
                            props.input.onChange(event.target.value);
                            form.change(FORM.FIELDS.ADDRESS_KEY.KEY, null);
                            setIsInternational(
                              event.target.value !== STRINGS.GB
                            );
                          }}
                        />
                      </FormControl>
                    )}
                  </Field>

                  <FormInput
                    name={FORM.FIELDS.WEIGHT.KEY}
                    label={FORM.FIELDS.WEIGHT.LABEL}
                    fieldProps={{
                      parse: value =>
                        NormalizerHelpers.formatAndConstrainDecimal({
                          maxIntegerLength: 2,
                          maxDecimalLength: 2,
                          minValue: 0.1,
                          value,
                        }),
                      format: StringUtil.formatDecimalString,
                      formatOnBlur: true,
                      validate: value => {
                        if (Number(value) > 30) {
                          return STRINGS.WEIGHT_MUST_BE_LESS_THAN_30KG;
                        }
                        if (value && Number(value) < 0.1) {
                          return STRINGS.WEIGHT_MUST_BE_MORE_THAN_0KG;
                        }
                      },
                    }}
                    variant='outlined'
                    size='small'
                    autoComplete='off'
                    sx={{
                      minWidth: { md: 158, xs: '100%' },
                      flexGrow: { xs: 1, md: 0 },
                      '& .MuiFormHelperText-root': { mt: 0.25, mx: 0 },
                    }}
                    helperText={STRINGS.MAX_PARCEL_WEIGHT_$(30)}
                    disabled={sameDayDelivery}
                  />

                  <Box
                    sx={{
                      minWidth: { md: 0 },
                      flex: { md: 1 },
                      width: { xs: '100%' },
                    }}
                  >
                    <Field
                      name={FORM.FIELDS.ADDRESS_KEY.KEY}
                      maxLength={8}
                      validate={required}
                      aria-controls={FORM.FIELDS.ADDRESS_KEY.KEY}
                      id={FORM.FIELDS.ADDRESS_KEY.KEY}
                    >
                      {props => (
                        <AddressLookup
                          {...props.input}
                          autocompleteInputId='address-key'
                          onChange={fullAddress => {
                            props.input.onChange(fullAddress?.Key);

                            form.batch(() => {
                              if (!fullAddress?.Key) {
                                form.change(
                                  FORM.SHIPMENT_FIELDS.DESTINATION_ORGANISATION
                                    .KEY,
                                  ''
                                );
                                form.change(
                                  FORM.SHIPMENT_FIELDS.DESTINATION_STREET.KEY,
                                  ''
                                );
                                form.change(
                                  FORM.SHIPMENT_FIELDS.DESTINATION_LOCALITY.KEY,
                                  ''
                                );
                                form.change(
                                  FORM.SHIPMENT_FIELDS.DESTINATION_CITY.KEY,
                                  ''
                                );
                                form.change(
                                  FORM.SHIPMENT_FIELDS.DESTINATION_COUNTY.KEY,
                                  ''
                                );
                                form.change(
                                  FORM.SHIPMENT_FIELDS.DESTINATION_POSTCODE.KEY,
                                  ''
                                );
                                return;
                              }
                              const { addressLine1, addressLine2 } =
                                AddressHelpers.transformAddressValues({
                                  street: fullAddress.Street,
                                  locality: fullAddress.Locality,
                                  property: fullAddress.Property,
                                });
                              form.change(
                                FORM.SHIPMENT_FIELDS.DESTINATION_ORGANISATION
                                  .KEY,
                                fullAddress.Organisation || ''
                              );
                              form.change(
                                FORM.SHIPMENT_FIELDS.DESTINATION_STREET.KEY,
                                addressLine1 || ''
                              );
                              form.change(
                                FORM.SHIPMENT_FIELDS.DESTINATION_LOCALITY.KEY,
                                addressLine2 || ''
                              );
                              form.change(
                                FORM.SHIPMENT_FIELDS.DESTINATION_CITY.KEY,
                                fullAddress.Town
                              );
                              form.change(
                                FORM.SHIPMENT_FIELDS.DESTINATION_COUNTY.KEY,
                                fullAddress.PostalCounty
                              );
                              form.change(
                                FORM.SHIPMENT_FIELDS.DESTINATION_POSTCODE.KEY,
                                countryISO === 'US'
                                  ? fullAddress.Postcode?.slice(0, 5)
                                  : fullAddress.Postcode
                              );
                            });
                          }}
                          label={
                            isInternational || sameDayDelivery
                              ? FORM.FIELDS.ADDRESS_KEY.LABEL_REQUIRED
                              : FORM.FIELDS.ADDRESS_KEY.LABEL
                          }
                          variant='outlined'
                          sx={{ width: '100%' }}
                          addressKey={props.input.value}
                          countryISO={countryISO}
                          inputSize='small'
                          shouldClearOnChange
                          required={isInternational || sameDayDelivery}
                        />
                      )}
                    </Field>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: { xs: 1.5, sm: 1.5 },
                    px: 2,
                    pt: 0.5,
                    pb: 1.5,
                  }}
                >
                  {sameday && (
                    <Field name='sameDayDelivery' type='checkbox'>
                      {({ input }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              name={input.name}
                              checked={!!input.checked}
                              color='primary'
                              onBlur={input.onBlur}
                              onChange={e => {
                                input.onChange(e);
                                if (e.target.checked) {
                                  const currentCountry = get(
                                    values,
                                    FORM.SHIPMENT_FIELDS.DESTINATION_COUNTRY.KEY
                                  );
                                  form.batch(() => {
                                    form.change(
                                      FORM.SHIPMENT_FIELDS.DESTINATION_COUNTRY
                                        .KEY,
                                      STRINGS.GB
                                    );
                                    form.change(
                                      FORM.FIELDS.WEIGHT.KEY,
                                      undefined
                                    );
                                    if (currentCountry !== STRINGS.GB) {
                                      form.change(
                                        FORM.FIELDS.ADDRESS_KEY.KEY,
                                        null
                                      );
                                    }
                                  });
                                  setCountryISO(STRINGS.GB);
                                  setIsInternational(false);
                                } else {
                                  form.batch(() => {
                                    form.change(
                                      FORM.SHIPMENT_FIELDS
                                        .COLLECTION_ADDRESS_KEY.KEY,
                                      undefined
                                    );
                                    form.change(
                                      FORM.SHIPMENT_FIELDS.COLLECTION_CITY.KEY,
                                      undefined
                                    );
                                    form.change(
                                      FORM.SHIPMENT_FIELDS.COLLECTION_COUNTY
                                        .KEY,
                                      undefined
                                    );
                                    form.change(
                                      FORM.SHIPMENT_FIELDS.COLLECTION_POSTCODE
                                        .KEY,
                                      undefined
                                    );
                                  });
                                }
                              }}
                              inputProps={{
                                'aria-label': STRINGS.SAME_DAY_DELIVERY,
                              }}
                            />
                          }
                          label={
                            <Typography
                              component='span'
                              sx={{
                                fontSize: 16,
                                color: 'text.primary',
                              }}
                            >
                              {STRINGS.SAME_DAY_DELIVERY}
                            </Typography>
                          }
                          sx={{ m: 0, alignItems: 'center' }}
                        />
                      )}
                    </Field>
                  )}

                  {sameDayDelivery ? (
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: { xs: '100%', sm: 200 },
                        minHeight: 0,
                      }}
                    >
                      <Field
                        name={FORM.SHIPMENT_FIELDS.COLLECTION_ADDRESS_KEY.KEY}
                        maxLength={8}
                        validate={validateCollectionAddressWhenSameDay}
                        aria-controls='form-collection-address'
                        id='form-collection-address'
                      >
                        {props => (
                          <AddressLookup
                            {...props.input}
                            autocompleteInputId='collection-address-key'
                            onChange={fullAddress => {
                              props.input.onChange(fullAddress?.Key);

                              form.batch(() => {
                                if (!fullAddress?.Key) {
                                  form.change(
                                    FORM.SHIPMENT_FIELDS.COLLECTION_ORGANISATION
                                      .KEY,
                                    ''
                                  );
                                  form.change(
                                    FORM.SHIPMENT_FIELDS.COLLECTION_STREET.KEY,
                                    ''
                                  );
                                  form.change(
                                    FORM.SHIPMENT_FIELDS.COLLECTION_LOCALITY
                                      .KEY,
                                    ''
                                  );
                                  form.change(
                                    FORM.SHIPMENT_FIELDS.COLLECTION_CITY.KEY,
                                    ''
                                  );
                                  form.change(
                                    FORM.SHIPMENT_FIELDS.COLLECTION_COUNTY.KEY,
                                    ''
                                  );
                                  form.change(
                                    FORM.SHIPMENT_FIELDS.COLLECTION_POSTCODE
                                      .KEY,
                                    ''
                                  );
                                  return;
                                }
                                const { addressLine1, addressLine2 } =
                                  AddressHelpers.transformAddressValues({
                                    street: fullAddress.Street,
                                    locality: fullAddress.Locality,
                                    property: fullAddress.Property,
                                  });
                                form.change(
                                  FORM.SHIPMENT_FIELDS.COLLECTION_ORGANISATION
                                    .KEY,
                                  fullAddress.Organisation || ''
                                );
                                form.change(
                                  FORM.SHIPMENT_FIELDS.COLLECTION_STREET.KEY,
                                  addressLine1 || ''
                                );
                                form.change(
                                  FORM.SHIPMENT_FIELDS.COLLECTION_LOCALITY.KEY,
                                  addressLine2 || ''
                                );
                                form.change(
                                  FORM.SHIPMENT_FIELDS.COLLECTION_CITY.KEY,
                                  fullAddress.Town
                                );
                                form.change(
                                  FORM.SHIPMENT_FIELDS.COLLECTION_COUNTY.KEY,
                                  fullAddress.PostalCounty
                                );
                                form.change(
                                  FORM.SHIPMENT_FIELDS.COLLECTION_POSTCODE.KEY,
                                  countryISO === 'US'
                                    ? fullAddress.Postcode?.slice(0, 5)
                                    : fullAddress.Postcode
                                );
                              });
                            }}
                            label={STRINGS.COLLECTION_ADDRESS}
                            variant='outlined'
                            sx={{ width: '100%' }}
                            addressKey={props.input.value}
                            countryISO={STRINGS.GB}
                            inputSize='small'
                            shouldClearOnChange
                            required
                          />
                        )}
                      </Field>
                    </Box>
                  ) : null}

                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: { xs: 1, sm: 1.5 },
                      flexShrink: 0,
                      ml: { xs: 0, md: sameDayDelivery ? 0 : 'auto' },
                      width: { xs: '100%', md: 'auto' },
                      justifyContent: { xs: 'flex-start', md: 'flex-end' },
                    }}
                  >
                    <Button
                      type='button'
                      variant='contained'
                      color='primary'
                      size='medium'
                      sx={{
                        textTransform: 'uppercase',
                        fontSize: 14,
                        fontWeight: 500,
                        letterSpacing: 0.4,
                      }}
                      disabled={
                        !!get(errors, FORM.FIELDS.WEIGHT.KEY) ||
                        (isInternational && invalid) ||
                        sameDayAddressesIncomplete
                      }
                      onClick={() => handleQuickQuoteClick(values)}
                    >
                      {STRINGS.GET_QUOTE}
                    </Button>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: { xs: 1, sm: 1.5 },
                      }}
                    >
                      <Button
                        component={Link}
                        to={ROUTE_PATH.TRACK}
                        variant='text'
                        color='secondary'
                        size='medium'
                        sx={{
                          textTransform: 'uppercase',
                          fontSize: 13,
                          fontWeight: 500,
                          letterSpacing: 0.46,
                          color: 'text.primary',
                        }}
                      >
                        {STRINGS.TRACK_A_PARCEL}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Paper>
          );
        }}
      />
      <ServicePricesModal
        open={serviceModalOpen}
        onClose={() => setServiceModalOpen(false)}
        onSubmit={networkRow => {
          setServiceModalOpen(false);
          const networkCode =
            networkRow && typeof networkRow === 'object'
              ? networkRow.networkCode
              : networkRow;
          if (quoteValues.sameDayDelivery) {
            dispatch(
              enterCitysprintWizardFromQuickQuote(quoteValues, networkRow)
            );
            navigate(SEND_A_CITYSPRINT_PARCEL);
            return;
          }
          handleQuoteFormSubmit({ ...quoteValues, networkCode });
        }}
        values={quoteValues}
        isInternational={isInternational}
        sameDayDelivery={!!quoteValues.sameDayDelivery}
        priceWithVatRateText={priceWithVatRateText}
      />
    </Box>
  );
};

export default QuoteForm;

QuoteForm.propTypes = {
  priceWithVatRateText: PropTypes.string.isRequired,
  priceWithoutVatRateText: PropTypes.string,
};
